import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";

// Local helper — silences debug logs in production while keeping them in dev.
const isDev = typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";
const debugLog = (...args) => { if (isDev) console.debug(...args); };

const PlayerContext = createContext(null);

const LS_HZ = "bbm_healing_hz";

const parseYouTubeId = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;
      // /embed/<id>, /shorts/<id>, /live/<id>
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => p === "embed" || p === "shorts" || p === "live");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    }
  } catch (_e) {
    return null;
  }
  return null;
};

// Load YouTube IFrame API once, resolve when ready.
let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return ytApiPromise;
};

// Deterministic Fisher-Yates shuffle seeded by ET calendar day — same order for all
// listeners within the same day; new order at every ET midnight so multi-track blocks
// rotate their lineup every day.
const _etDayFormatter = typeof Intl !== "undefined" && Intl.DateTimeFormat
  ? new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
  : null;

const etDayNumber = (d) => {
  // Returns YYYYMMDD as an integer in Eastern time — stable across visitor timezones.
  const date = d instanceof Date ? d : new Date(d);
  if (!_etDayFormatter) {
    return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  }
  const parts = _etDayFormatter.formatToParts(date).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  return parseInt(parts.year, 10) * 10000 + parseInt(parts.month, 10) * 100 + parseInt(parts.day, 10);
};

const dailyShuffle = (arr, date, salt = 0) => {
  const a = Array.isArray(arr) ? arr.slice() : [];
  if (a.length < 2) return a;
  let seed = (etDayNumber(date) + salt) >>> 0;
  const rand = () => {
    seed ^= seed << 13; seed >>>= 0;
    seed ^= seed >>> 17;
    seed ^= seed << 5; seed >>>= 0;
    return (seed >>> 0) / 0xffffffff;
  };
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Return { hour, minute, weekday } in America/New_York (ET) regardless of the visitor's timezone.
// Uses Intl.DateTimeFormat so DST (EST↔EDT) is handled automatically.
const _etFormatter = typeof Intl !== "undefined" && Intl.DateTimeFormat
  ? new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
  : null;

const easternParts = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (!_etFormatter) {
    return { hour: d.getHours(), minute: d.getMinutes(), weekday: d.getDay(), etDate: d };
  }
  const parts = _etFormatter.formatToParts(d).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  // Intl reports hour "24" at midnight — normalize to 0.
  const hour = parseInt(parts.hour, 10) % 24;
  const minute = parseInt(parts.minute, 10);
  const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const weekday = weekdayMap[parts.weekday] ?? 0;
  return { hour, minute, weekday };
};

// Compute what Deep Corners should be playing right now (or null if outside the 07:00–23:00 ET window).
const computeDeepCornersNow = (config, now) => {
  if (!config || config.enabled === false) return { active: false };
  const { hour, minute } = easternParts(now);
  if (hour < 7 || hour >= 23) return { active: false };
  const blockIdx = Math.floor((hour - 7) / 2);
  const isAd = minute >= 55;

  // Normalize block shape: array (legacy) OR { name, tracks } (current).
  const rawBlocks = Array.isArray(config.blocks) ? config.blocks : [];
  const raw = rawBlocks[blockIdx];
  const block = Array.isArray(raw)
    ? { name: "", tracks: raw }
    : raw && typeof raw === "object"
    ? { name: raw.name || "", tracks: Array.isArray(raw.tracks) ? raw.tracks : [] }
    : { name: "", tracks: [] };

  const label = block.name
    ? `Black Box Music · ${block.name}`
    : `Black Box Music · Block ${blockIdx + 1} of 8`;

  if (isAd) {
    return {
      active: true,
      url: config.intermission_url || "",
      title: `${label} · Intermission`,
      trackTitle: "Intermission — Sponsor Break",
      trackArtist: "Black Box Music",
      isAd: true,
      blockIdx,
      blockName: block.name,
    };
  }

  // Normalize each track: string URL or { url, title, artist }.
  const cleanTracks = block.tracks
    .map((t) => (typeof t === "string" ? { url: t, title: "", artist: "" } : t))
    .filter((t) => t && t.url && t.url.trim().length > 0);

  // Rotate ONLY the non-uploaded URL links (YouTube, Icecast streams, external MP3s)
  // daily. Uploaded MP3s keep their fixed order — the admin authored that order and
  // shuffling uploads confuses listeners tracking a specific song.
  const isUpload = (t) => t.url.includes("/api/uploads/audio/");
  const urlLinkPositions = [];
  const urlLinks = [];
  cleanTracks.forEach((t, i) => {
    if (!isUpload(t)) {
      urlLinkPositions.push(i);
      urlLinks.push(t);
    }
  });
  let shuffled = cleanTracks.slice();
  if (urlLinks.length > 1) {
    const rotated = dailyShuffle(urlLinks, now, blockIdx);
    urlLinkPositions.forEach((pos, k) => { shuffled[pos] = rotated[k]; });
  }
  return {
    active: true,
    // Full shuffled playlist — the caller picks the current track via a rotating index that
    // advances on 'ended' events, so shorter files rotate through all tracks within the hour.
    playlist: shuffled,
    title: label,
    isAd: false,
    blockIdx,
    blockName: block.name,
  };
};

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const ytHostRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const playIntentRef = useRef(false);
  // Callable ref that returns true if we advanced to a new deep-corners track, false otherwise.
  // Kept in a ref so YouTube iframe callbacks (bound at creation) always see current state.
  const advanceDeepCornersRef = useRef(() => false);

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [streamUrl, setStreamUrl] = useState("");
  const [healingLinks, setHealingLinks] = useState({});
  const [inBlock, setInBlock] = useState(false);
  // When set, forces the persistent player to play this specific healing-frequency URL
  // regardless of the schedule (Deep Corners block, main stream, etc.). Used when a user
  // clicks a frequency box outside the 00:00–05:30 ET window so they hear it immediately.
  const [frequencyOverride, setFrequencyOverride] = useState(null);
  const [defaultHz, setDefaultHz] = useState(null);
  const [preferredHz, setPreferredHz] = useState(() => {
    const v = typeof window !== "undefined" ? localStorage.getItem(LS_HZ) : null;
    if (!v) return null;
    return /^\d+$/.test(v) ? parseInt(v, 10) : v;
  });
  const [deepCornersConfig, setDeepCornersConfig] = useState(null);
  const [deepCornersNow, setDeepCornersNow] = useState({ active: false, isAd: false });
  // Rotating index into deepCornersNow.playlist — advances on `ended` events so multi-track
  // blocks rotate through all tracks within a single hour. Resets when the block changes.
  const [blockPlaylistIdx, setBlockPlaylistIdx] = useState(0);
  const [nowPlaying, setNowPlaying] = useState({
    title: "Tuning in...",
    artist: "Black Box Radio",
    cover_url: "",
    show_title: "",
    is_live: true,
  });

  // Bootstrap settings + healing metadata
  useEffect(() => {
    api.get("/settings").then((r) => {
      setStreamUrl(r.data.stream_url || "");
      setHealingLinks(r.data.healing_links || {});
      setDeepCornersConfig(r.data.deep_corners || null);
    }).catch(() => {});

    // We compute inBlock in Eastern Time (America/New_York) so every visitor gets the
    // same schedule regardless of their local timezone. Handles DST automatically.
    const computeInBlock = () => {
      const { hour, minute } = easternParts(new Date());
      return hour < 5 || (hour === 5 && minute < 30);
    };

    const refreshHealing = () => {
      setInBlock(computeInBlock());
      api.get("/healing-frequencies").then((r) => {
        setDefaultHz(r.data.default_hz);
      }).catch(() => {});
    };
    refreshHealing();
    const id = setInterval(refreshHealing, 30000);
    return () => clearInterval(id);
  }, []);

  // Poll now-playing with the chosen hz
  useEffect(() => {
    const q =
      preferredHz == null
        ? ""
        : typeof preferredHz === "number"
        ? `?hz=${preferredHz}`
        : "";
    const poll = () => {
      api.get(`/now-playing${q}`).then((r) => setNowPlaying(r.data)).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, [preferredHz]);

  // Deep Corners scheduler — recompute every 15s so hour boundaries + ad breaks flip cleanly.
  // Also reset the rotating playlist index when the block changes so each block starts at track 0.
  useEffect(() => {
    const tick = () => {
      const next = computeDeepCornersNow(deepCornersConfig, new Date());
      setDeepCornersNow((prev) => {
        if (next.active && !next.isAd && prev.blockIdx !== next.blockIdx) {
          setBlockPlaylistIdx(0);
        }
        return next;
      });
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [deepCornersConfig]);

  // The currently-selected track from the shuffled Deep Corners playlist (if any).
  const currentDeepTrack = useMemo(() => {
    if (!deepCornersNow.active || deepCornersNow.isAd) return null;
    const pl = deepCornersNow.playlist || [];
    if (pl.length === 0) return null;
    return pl[blockPlaylistIdx % pl.length];
  }, [deepCornersNow, blockPlaylistIdx]);

  // Overlay Now Playing metadata when Deep Corners is currently on the air, or when
  // the user has manually invoked a healing frequency override.
  const displayedNowPlaying = useMemo(() => {
    if (frequencyOverride) {
      // Find the healing key (e.g. "432" or "handpan") whose URL matches the override.
      const key = Object.keys(healingLinks).find((k) => (healingLinks[k] || "").trim() === frequencyOverride);
      let label = "Overnight Healing";
      if (key) {
        label = /^\d+$/.test(key) ? `${key} Hz — Healing` : `${key.charAt(0).toUpperCase()}${key.slice(1)} — Healing`;
      }
      return {
        title: label,
        artist: "Black Box Music · Overnight Healing",
        cover_url: nowPlaying.cover_url || "",
        show_title: "Overnight Healing · On Demand",
        is_live: true,
      };
    }
    if (!inBlock && deepCornersNow.active) {
      if (deepCornersNow.isAd) {
        return {
          title: "Intermission — Sponsor Break",
          artist: "Black Box Music",
          cover_url: nowPlaying.cover_url || "",
          show_title: `${deepCornersNow.title || "Black Box Music"} · Intermission`,
          is_live: true,
        };
      }
      return {
        title: (currentDeepTrack && currentDeepTrack.title) || "Black Box Music Programming",
        artist: (currentDeepTrack && currentDeepTrack.artist) || "Black Box Music",
        cover_url: nowPlaying.cover_url || "",
        show_title: deepCornersNow.title,
        is_live: true,
      };
    }
    return nowPlaying;
  }, [inBlock, deepCornersNow, nowPlaying, currentDeepTrack, frequencyOverride, healingLinks]);

  // Keep the advance-ref in sync so audio.onEnded and YT.onStateChange (ended) can call it.
  // Returns true if a new track was selected, false if there was nothing to advance to.
  advanceDeepCornersRef.current = () => {
    if (!deepCornersNow.active || deepCornersNow.isAd) return false;
    const pl = deepCornersNow.playlist || [];
    if (pl.length < 2) return false; // Single-track blocks fall through to native looping.
    setBlockPlaylistIdx((i) => (i + 1) % pl.length);
    return true;
  };

  // Compute effective source. Priority: manual frequency override → healing block → Deep Corners → main stream.
  const { activeSource, isYouTube } = useMemo(() => {
    let src = streamUrl;
    if (frequencyOverride && frequencyOverride.trim().length > 0) {
      src = frequencyOverride.trim();
    } else if (inBlock) {
      // Numeric Hz → fall back to nightly default. Non-numeric key (e.g. "handpan") is used as-is.
      const key = preferredHz != null ? preferredHz : defaultHz;
      const candidate = key != null ? healingLinks[String(key)] : "";
      if (candidate && candidate.trim().length > 0) src = candidate.trim();
    } else if (deepCornersNow.active) {
      if (deepCornersNow.isAd) {
        // Intermission (5-min ad reel). Fall through to main stream if not configured.
        const adUrl = (deepCornersNow.url || "").trim();
        if (adUrl.length > 0) src = adUrl;
      } else if (currentDeepTrack && currentDeepTrack.url) {
        src = currentDeepTrack.url.trim();
      }
    }
    const ytId = parseYouTubeId(src);
    return { activeSource: src, isYouTube: !!ytId, ytId };
  }, [inBlock, preferredHz, defaultHz, healingLinks, streamUrl, deepCornersNow, currentDeepTrack, frequencyOverride]);

  // Keep HTMLAudio in sync
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
    audioRef.current.muted = muted;
    if (ytPlayerRef.current && ytPlayerRef.current.setVolume) {
      try {
        ytPlayerRef.current.setVolume(Math.round(volume * 100));
        if (muted) ytPlayerRef.current.mute();
        else ytPlayerRef.current.unMute();
      } catch (e) { debugLog("[YT] volume/mute failed", e); }
    }
  }, [volume, muted]);

  // Manage YouTube player lifecycle when source changes
  useEffect(() => {
    let cancelled = false;

    const teardownYt = () => {
      if (ytPlayerRef.current && ytPlayerRef.current.destroy) {
        try { ytPlayerRef.current.destroy(); } catch (e) { debugLog("[YT] destroy failed", e); }
      }
      ytPlayerRef.current = null;
      if (ytHostRef.current) {
        while (ytHostRef.current.firstChild) {
          ytHostRef.current.removeChild(ytHostRef.current.firstChild);
        }
      }
    };

    if (!isYouTube) {
      teardownYt();
      return;
    }

    const ytId = parseYouTubeId(activeSource);
    if (!ytId) {
      teardownYt();
      return;
    }

    loadYouTubeApi().then((YT) => {
      if (cancelled || !YT || !ytHostRef.current) return;
      teardownYt();
      const container = document.createElement("div");
      container.id = `yt-player-${Date.now()}`;
      ytHostRef.current.appendChild(container);
      ytPlayerRef.current = new YT.Player(container.id, {
        videoId: ytId,
        playerVars: {
          autoplay: playIntentRef.current ? 1 : 0,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          loop: 1,
          playlist: ytId,
        },
        events: {
          onReady: (e) => {
            try {
              e.target.setVolume(Math.round(volume * 100));
              e.target.unMute();
              if (playIntentRef.current) {
                setMuted(false);
                setPlaying(true);
                e.target.playVideo();
              }
            } catch (err) { debugLog("[YT] onReady failed", err); }
          },
          onStateChange: (e) => {
            // 0=ended, 1=playing, 2=paused, 3=buffering
            if (e.data === 1) setPlaying(true);
            if (e.data === 2) setPlaying(false);
            if (e.data === 0 && ytPlayerRef.current) {
              // If we're in a multi-track Deep Corners block, advance to next track;
              // otherwise fall back to native single-video loop.
              const advanced = advanceDeepCornersRef.current();
              if (!advanced) {
                try { ytPlayerRef.current.playVideo(); } catch (err) { debugLog("[YT] loop replay failed", err); }
              }
            }
          },
        },
      });
    });

    return () => { cancelled = true; };
  }, [isYouTube, activeSource]);

  const toggle = async () => {
    const nextPlaying = !playing;
    playIntentRef.current = nextPlaying;
    setPlaying(nextPlaying);

    if (isYouTube) {
      const p = ytPlayerRef.current;
      if (p) {
        try {
          setMuted(false);
          p.unMute();
          if (nextPlaying) p.playVideo();
          else p.pauseVideo();
        } catch (e) { debugLog("[YT] toggle failed", e); }
      }
      return;
    }

    if (!audioRef.current) return;
    if (nextPlaying) {
      try {
        setMuted(false);
        audioRef.current.muted = false;
        await audioRef.current.play();
      } catch (e) {
        debugLog("[Audio] play() rejected", e);
        setPlaying(false);
        playIntentRef.current = false;
      }
    } else {
      audioRef.current.pause();
    }
  };

  const play = async () => {
    if (playing) return;
    await toggle();
  };

  // Set a preferred Hz AND start playback of that source. Used by the frequency picker.
  const playFrequency = (hz) => {
    playIntentRef.current = true;
    setMuted(false);
    setPlaying(true);
    setPreferredHzPersist(hz);
    // Resolve the frequency to its stream URL and pin it as the active override so the
    // persistent player switches over right now, regardless of the block schedule.
    const key = hz != null ? String(hz) : null;
    const url = key ? (healingLinks[key] || "").trim() : "";
    setFrequencyOverride(url && url.length > 0 ? url : null);
    // If YT player already exists for the same source, kick it now.
    // Otherwise, the source-change effect will (re)create it and onReady auto-plays via playIntentRef.
    if (ytPlayerRef.current) {
      try {
        ytPlayerRef.current.unMute();
        ytPlayerRef.current.playVideo();
      } catch (e) { debugLog("[YT] playFrequency failed", e); }
    }
    if (audioRef.current && !isYouTube) {
      audioRef.current.muted = false;
      audioRef.current.play().catch((e) => { debugLog("[Audio] playFrequency play() rejected", e); });
    }
  };

  const clearFrequencyOverride = () => setFrequencyOverride(null);

  // If user changes preferred hz and player is currently playing, restart playback on the new source.
  useEffect(() => {
    if (!playing) return;
    if (isYouTube) {
      // YT player will be (re)created by the source-change effect; onReady kicks off play.
      return;
    }
    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.play().catch(() => setPlaying(false));
    }
  }, [activeSource]);

  const setPreferredHzPersist = (hz) => {
    setPreferredHz(hz);
    if (hz == null) localStorage.removeItem(LS_HZ);
    else localStorage.setItem(LS_HZ, String(hz));
  };

  // Memoize context value so consumers only re-render on real state changes.
  const contextValue = useMemo(
    () => ({
      playing,
      muted,
      volume,
      setVolume,
      setMuted,
      toggle,
      play,
      playFrequency,
      clearFrequencyOverride,
      nowPlaying: displayedNowPlaying,
      streamUrl,
      preferredHz,
      defaultHz,
      inBlock,
      setPreferredHz: setPreferredHzPersist,
      activeSource,
      isYouTube,
      deepCornersNow,
    }),
    [
      playing, muted, volume, displayedNowPlaying, streamUrl,
      preferredHz, defaultHz, inBlock, activeSource, isYouTube, deepCornersNow,
    ]
  );

  return (
    <PlayerContext.Provider value={contextValue}>
      {children}
      {/* Hidden YouTube host */}
      <div
        ref={ytHostRef}
        aria-hidden
        className="pointer-events-none fixed"
        style={{ left: -9999, top: -9999, width: 1, height: 1, opacity: 0 }}
      />
      {/* Direct-audio element (used when active source is NOT YouTube).
          No `crossOrigin` — many Icecast/Shoutcast streams (Frisky, 181fm, 1.FM) don't send
          CORS headers, and setting crossOrigin="anonymous" causes the browser to silently
          reject them. We're not reading raw samples via Web Audio API, so we don't need it.
          Loop behavior:
            • Multi-track Deep Corners block → loop=false + onEnded advances to the next track.
            • Everything else (single-track blocks, healing, main stream) → loop=true so finite
              files replay for the full slot; streams ignore loop since they never end. */}
      {activeSource && !isYouTube ? (
        <audio
          ref={audioRef}
          src={activeSource}
          preload="none"
          loop={!(deepCornersNow.active && !deepCornersNow.isAd && (deepCornersNow.playlist || []).length > 1)}
          onEnded={() => { advanceDeepCornersRef.current(); }}
          data-testid="radio-audio-element"
        />
      ) : null}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
